import React, { useState, useEffect, useRef, forwardRef } from 'react';
import { Input } from './input';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';

interface AddressAutocompleteProps extends React.InputHTMLAttributes<HTMLInputElement> {
  onAddressSelect: (address: {
    fullAddress: string;
    city?: string;
    state?: string;
    zipCode?: string;
    lat?: number;
    lon?: number;
  }) => void;
}

export const AddressAutocomplete = forwardRef<HTMLInputElement, AddressAutocompleteProps>(
  ({ className, onAddressSelect, ...props }, forwardedRef) => {
    const [query, setQuery] = useState(props.value ? String(props.value) : '');
    const [suggestions, setSuggestions] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const suggestionRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Use the forwarded ref if provided, otherwise use our internal ref
    const resolvedRef = (forwardedRef as React.RefObject<HTMLInputElement>) || inputRef;
    
    // Update internal state when props.value changes
    useEffect(() => {
      if (props.value !== undefined) {
        setQuery(String(props.value));
      }
    }, [props.value]);

    useEffect(() => {
      // Close suggestions when clicking outside
      const handleClickOutside = (event: MouseEvent) => {
        if (
          containerRef.current && 
          !containerRef.current.contains(event.target as Node)
        ) {
          setSuggestions([]);
          setIsFocused(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
      const fetchSuggestions = async () => {
        if (query.length < 3) {
          setSuggestions([]);
          return;
        }

        setIsLoading(true);
        try {
          console.log("Fetching address suggestions for:", query);
          const response = await fetch(
            `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
              query
            )}&countrycodes=us&addressdetails=1&limit=5`,
            {
              headers: {
                "Accept-Language": "en-US,en;q=0.9",
                "User-Agent": "AssociationManagementApp"
              }
            }
          );
          
          if (response.ok) {
            const data = await response.json();
            console.log('OpenStreetMap API response:', data);
            setSuggestions(data);
          } else {
            console.error('Error response from OpenStreetMap:', response.status, response.statusText);
            const text = await response.text();
            console.error('Response body:', text);
          }
        } catch (error) {
          console.error('Error fetching address suggestions:', error);
        } finally {
          setIsLoading(false);
        }
      };

      // Debounce API calls
      const timeoutId = setTimeout(() => {
        if (query.length >= 3) {
          fetchSuggestions();
        }
      }, 500); // Increased debounce time for better UX

      return () => clearTimeout(timeoutId);
    }, [query]);

    const handleSelect = (suggestion: any) => {
      console.log('Selected suggestion:', suggestion);
      
      // Extract address components
      const address = suggestion.address;
      let state = '';
      let zipCode = '';
      let city = '';
      let streetAddress = '';
      
      // Map OpenStreetMap data to our format
      if (address) {
        state = address.state_code || address.state || '';
        zipCode = address.postcode || '';
        
        // Try multiple fields for city, as OpenStreetMap can use different fields
        city = address.city || 
               address.town || 
               address.village || 
               address.municipality || 
               address.hamlet || 
               address.county || 
               '';
               
        // Extract just the street address portion
        streetAddress = address.house_number ? 
          `${address.house_number} ${address.road || ''}`.trim() : 
          address.road || '';
        
        console.log("Extracted city from response:", city);
        console.log("Extracted street address:", streetAddress);
      }

      // If no structured street address found, extract from display_name
      if (!streetAddress && suggestion.display_name) {
        const parts = suggestion.display_name.split(',');
        if (parts.length > 0) {
          // Take first part as street address (typically house number + street)
          streetAddress = parts[0].trim();
          console.log("Using first part of display_name as street address:", streetAddress);
        }
      }

      // If state is full name, try to convert to abbreviation (simple matching)
      if (state && state.length > 2) {
        const states: {[key: string]: string} = {
          'alabama': 'AL', 'alaska': 'AK', 'arizona': 'AZ', 'arkansas': 'AR', 'california': 'CA',
          'colorado': 'CO', 'connecticut': 'CT', 'delaware': 'DE', 'florida': 'FL', 'georgia': 'GA',
          'hawaii': 'HI', 'idaho': 'ID', 'illinois': 'IL', 'indiana': 'IN', 'iowa': 'IA',
          'kansas': 'KS', 'kentucky': 'KY', 'louisiana': 'LA', 'maine': 'ME', 'maryland': 'MD',
          'massachusetts': 'MA', 'michigan': 'MI', 'minnesota': 'MN', 'mississippi': 'MS', 'missouri': 'MO',
          'montana': 'MT', 'nebraska': 'NE', 'nevada': 'NV', 'new hampshire': 'NH', 'new jersey': 'NJ',
          'new mexico': 'NM', 'new york': 'NY', 'north carolina': 'NC', 'north dakota': 'ND', 'ohio': 'OH',
          'oklahoma': 'OK', 'oregon': 'OR', 'pennsylvania': 'PA', 'rhode island': 'RI', 'south carolina': 'SC',
          'south dakota': 'SD', 'tennessee': 'TN', 'texas': 'TX', 'utah': 'UT', 'vermont': 'VT',
          'virginia': 'VA', 'washington': 'WA', 'west virginia': 'WV', 'wisconsin': 'WI', 'wyoming': 'WY',
          'district of columbia': 'DC'
        };
        
        const stateAbbr = states[state.toLowerCase()];
        if (stateAbbr) {
          state = stateAbbr;
        }
      }
      
      // Update the query to only show the street address
      setQuery(streetAddress);
      setSuggestions([]);
      setIsFocused(false);
      
      if (resolvedRef.current) {
        resolvedRef.current.value = streetAddress;
      }

      // Call the onAddressSelect callback with all the data
      onAddressSelect({
        fullAddress: streetAddress, // Only pass the street address as fullAddress
        city,
        state,
        zipCode,
        lat: parseFloat(suggestion.lat),
        lon: parseFloat(suggestion.lon)
      });
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setQuery(value);
      console.log("Address input changed:", value);
      
      if (props.onChange) {
        props.onChange(e);
      }
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      if (props.onFocus) {
        props.onFocus(e);
      }
    };

    return (
      <div className="relative w-full" ref={containerRef}>
        <Input
          ref={resolvedRef}
          type="text"
          className={cn("w-full", className)}
          placeholder={props.placeholder || "Enter an address"}
          onChange={handleChange}
          onFocus={handleFocus}
          value={query}
          name={props.name}
          {...props}
        />
        {isLoading && isFocused && (
          <div className="absolute right-3 top-3">
            <Loader2 className="animate-spin h-4 w-4 text-gray-500" />
          </div>
        )}
        {suggestions.length > 0 && isFocused && (
          <div 
            ref={suggestionRef}
            className="absolute z-10 w-full bg-white mt-1 rounded-md shadow-lg border border-gray-200 max-h-60 overflow-auto"
          >
            {suggestions.map((suggestion, index) => (
              <div
                key={index}
                className="px-4 py-2 cursor-pointer hover:bg-gray-100 text-sm"
                onClick={() => handleSelect(suggestion)}
                onMouseDown={(e) => e.preventDefault()} // Prevent input blur when clicking suggestion
              >
                {suggestion.display_name.replace(", United States", "")}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
);

AddressAutocomplete.displayName = "AddressAutocomplete";
